import React, { useState, useRef, useEffect, useContext } from 'react';
import { Container, Card, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaPaperPlane,
  FaPaperclip,
  FaSmile,
  FaCheck,
  FaCheckDouble,
  FaCircle,
  FaEllipsisV,
  FaFile,
  FaTimes,
  FaComments
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import '../../../../assets/css/customer/dashboard/CustomerChat.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';
import AppContext from '../../../../context/AppContext';
import { checkLoggedInCustomer } from '../../../../helper/Helper';

const CustomerChat = () => {

  const { baseUrl } = useContext(AppContext);
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [weconversationId, setWeconversationId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const emojis = ['😊', '😂', '❤️', '👍', '🎉', '🙏', '😍', '🔥', '✨', '💯', '👏', '🎊'];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // READ - fetch conversation + messages
  const viewMessages = (loggedInCustomer) => {
    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);

    axios.post(`${baseUrl}/customer/chat/viewMessages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          setMessages(res.data.messages);
          setWeconversationId(res.data.weconversation_id);
          setPageLoading(false);
          markMessagesAsRead(res.data.weconversation_id);
        }
      })
      .catch((err) => {
        console.log(err);
        setPageLoading(false);
      });
  };

  // UPDATE - mark admin messages as read
  const markMessagesAsRead = (convId) => {
    const formData = new FormData();
    formData.append('weconversation_id', convId);

    axios.post(`${baseUrl}/customer/chat/markAsRead`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => console.log(res.data))
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    checkLoggedInCustomer(navigate);

    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
    if (!loggedInCustomer) return;

    viewMessages(loggedInCustomer);
  }, []);

 
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const formData = new FormData();
    formData.append('weconversation_id', weconversationId);
    formData.append('message', messageText);

    axios.post(`${baseUrl}/customer/chat/sendMessage`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
          viewMessages(loggedInCustomer);
          setMessageText('');
          setUploadedFile(null);
          setShowEmojiPicker(false);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please upload a file smaller than 5MB',
          confirmButtonColor: '#7fd3c1'
        });
        return;
      }
      setUploadedFile({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type,
        url: URL.createObjectURL(file)
      });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEmojiClick = (emoji) => {
    setMessageText(messageText + emoji);
    setShowEmojiPicker(false);
  };

  return (
    <CustomerLayout>
      <div className="cch-chat-page">
        <Container fluid className="cch-container">

          {/* Page Header */}
          <div className="cch-page-header">
            <h1 className="cch-page-title">
              <FaComments className="cch-title-icon" />
              Messages
            </h1>
            <p className="cch-page-subtitle">Chat with admin and vendors</p>
          </div>

          
          <Card className="cch-chat-card">

            {/* Chat Header */}
            <div className="cch-chat-header">
              <div className="cch-chat-user-info">
                <img
                  src="https://ui-avatars.com/api/?name=Admin+Support&background=7fd3c1&color=fff"
                  alt="Admin Support"
                  className="cch-chat-avatar"
                />
                <div className="cch-chat-user-details">
                  <h5 className="cch-chat-user-name">Admin Support</h5>
                  <span className="cch-chat-status cch-status-online">
                    <FaCircle className="cch-status-icon" />
                    Online
                  </span>
                </div>
              </div>
              <div className="cch-header-actions">
                <button className="cch-options-btn">
                  <FaEllipsisV />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="cch-messages-area">
              {pageLoading ? (
                <p className="text-center">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Loading messages...
                </p>
              ) : messages.length === 0 ? (
                <p className="text-center text-muted">No messages yet. Say hi!</p>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`cch-message ${message.sender === 'customer' ? 'cch-message-sent' : 'cch-message-received'}`}
                  >
                    {message.sender !== 'customer' && (
                      <img
                        src="https://ui-avatars.com/api/?name=Admin+Support&background=7fd3c1&color=fff"
                        alt="Admin"
                        className="cch-message-avatar"
                      />
                    )}
                    <div className="cch-message-content">
                      {message.message && <p className="cch-message-text">{message.message}</p>}
                      <div className="cch-message-meta">
                        <span className="cch-message-time">{formatTime(message.created_at)}</span>
                        {message.sender === 'customer' && (
                          <span className="cch-message-status">
                            {message.status === 'read' ? (
                              <FaCheckDouble className="cch-read" />
                            ) : (
                              <FaCheck className="cch-delivered" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Message Input */}
            <div className="cch-message-input-wrapper">
              {uploadedFile && (
                <div className="cch-file-upload-preview">
                  {uploadedFile.type.startsWith('image/') ? (
                    <img
                      src={uploadedFile.url}
                      alt={uploadedFile.name}
                      className="cch-preview-image"
                    />
                  ) : (
                    <div className="cch-preview-file">
                      <FaFile className="cch-preview-icon" />
                      <div className="cch-preview-info">
                        <span className="cch-preview-name">{uploadedFile.name}</span>
                        <span className="cch-preview-size">{uploadedFile.size}</span>
                      </div>
                    </div>
                  )}
                  <button className="cch-remove-file-btn" onClick={handleRemoveFile}>
                    <FaTimes />
                  </button>
                </div>
              )}

              <Form onSubmit={handleSendMessage} className="cch-message-form">
                <div className="cch-input-actions">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <Button
                    variant="link"
                    className="cch-action-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach File"
                  >
                    <FaPaperclip />
                  </Button>
                  <div className="cch-emoji-picker-wrapper">
                    <Button
                      variant="link"
                      className="cch-action-btn"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Add Emoji"
                    >
                      <FaSmile />
                    </Button>
                    {showEmojiPicker && (
                      <div className="cch-emoji-picker">
                        {emojis.map((emoji, index) => (
                          <span
                            key={index}
                            className="cch-emoji"
                            onClick={() => handleEmojiClick(emoji)}
                          >
                            {emoji}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Form.Control
                  type="text"
                  placeholder="Type a message..."
                  className="cch-message-input"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                />
                <Button
                  type="submit"
                  className="cch-send-btn"
                  disabled={!messageText.trim()}
                >
                  <FaPaperPlane />
                </Button>
              </Form>
            </div>

          </Card>

        </Container>
      </div>
    </CustomerLayout>
  );
};

export default CustomerChat;